import { combineEpics } from 'redux-observable';
import { Action } from '@reduxjs/toolkit';
import {
  concatMap,
  debounceTime,
  defer,
  filter,
  from,
  map,
  mergeMap,
  of,
  retry,
  switchMap,
  takeUntil,
  timer,
} from 'rxjs';

import module from './index';
import * as api from './cart_products.api';

import { catchAndLogError } from 'utils/common';

import type { Epic } from 'redux-observable';
import type { CheckoutRootState } from 'store/checkout_store';
import type { RootState } from 'store/wos_store';

const { actions } = module;

type CartProductEpic = Epic<Action, Action, RootState | CheckoutRootState>;

/**
 * Fetch cart products
 */
const getAllCartProductsEpic: CartProductEpic = (action$) =>
  action$.pipe(
    filter(actions.getAll.pending.match),
    switchMap(() =>
      from(api.all()).pipe(
        map((cps) => actions.getAll.fulfilled(cps)),
        catchAndLogError(actions.getAll.rejected())
      )
    )
  );

/**
 * Sets up fetches & retries for cart product thumbnails
 */
const cartProductThumbnailEpic: CartProductEpic = (action$) =>
  action$.pipe(
    filter(actions.getAll.fulfilled.match),
    concatMap(({ payload }) => from(payload)), // turn the array into a stream
    filter((cp) => !cp.thumbnail_url), // filter to get ones missing thumb url
    // mergeMap to get these in parallel -- if want in series, use concatMap
    mergeMap((cp) =>
      // create observable for request, use defer to re-run the promise when retried
      defer(() => api.getThumbnail(cp.id)).pipe(
        retry({
          count: 5,
          delay: (error, retries) =>
            timer(5000 * retries).pipe(
              // Cancel the retries if this cart product is removed
              takeUntil(
                action$.pipe(
                  filter(actions.destroy.pending.match),
                  filter(({ payload }) => payload === cp.id)
                )
              )
            ),
        }),
        map((urls) => actions.update.fulfilled({ id: cp.id, ...urls })),
        catchAndLogError(actions.cartProductThumbnailError(cp.id))
      )
    )
  );

const updateCartProductQuantityEpic: CartProductEpic = (action$) =>
  action$.pipe(
    filter(actions.updateQuantity.pending.match),
    debounceTime(300),
    concatMap(({ payload }) => {
      if (payload.quantity <= 0) {
        return of(actions.destroy.pending(payload.id));
      } else {
        return from(api.update(payload)).pipe(
          concatMap(() => api.find(payload.id)),
          map(actions.updateQuantity.fulfilled),
          catchAndLogError(actions.updateQuantity.rejected())
        );
      }
    })
  );

const deleteCartProductEpic: CartProductEpic = (action$) =>
  action$.pipe(
    filter(actions.destroy.pending.match),
    concatMap(({ payload }) =>
      from(api.destroy(payload)).pipe(
        map(() => actions.destroy.fulfilled(payload)),
        catchAndLogError(actions.destroy.rejected())
      )
    )
  );

const cartProductEpics = combineEpics(
  getAllCartProductsEpic,
  cartProductThumbnailEpic,
  updateCartProductQuantityEpic,
  deleteCartProductEpic
);
export default cartProductEpics;
